import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/hooks/use-language';
import { usePageHero } from '@/hooks/usePageHero';
import { Filter, ChevronRight, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Product, LocalizedText, getCardSpecs, FormattedSpec } from '@/lib/products';
import { getCachedProducts, fetchProducts, subscribeToCache } from '@/lib/globalDataCache';
import { SEO } from '@/components/ui/SEO';
import { buildCollectionPage, buildBreadcrumbs } from '@/lib/schema';
import { CatalogFilter } from '@/components/catalog/CatalogFilter';
import { Pagination } from '@/components/ui/pagination';
import { trackClick } from '@/lib/analytics';

// ─── Natural sort helper ──────────────────────────────────────────────────────
function naturalSortByCode(a: Product, b: Product): number {
  const ax: Array<string | number> = [];
  const bx: Array<string | number> = [];
  // Guard: ensure code is always a string before calling .replace()
  const codeA = (typeof a.code === 'string' ? a.code : String(a.code ?? ''));
  const codeB = (typeof b.code === 'string' ? b.code : String(b.code ?? ''));
  codeA.replace(/(\d+)|(\D+)/g, (_, n, s) => { ax.push(n ? Number(n) : s); return ''; });
  codeB.replace(/(\d+)|(\D+)/g, (_, n, s) => { bx.push(n ? Number(n) : s); return ''; });
  for (let i = 0; i < Math.max(ax.length, bx.length); i++) {
    if (ax[i] === undefined) return -1;
    if (bx[i] === undefined) return 1;
    if (ax[i] < bx[i]) return -1;
    if (ax[i] > bx[i]) return 1;
  }
  return 0;
}

// ─── Normalize laying type to canonical key ───────────────────────────────────
// DB may store Russian strings OR i18n keys — normalize to canonical set
function normalizeLayingType(raw: string): string {
  const s = (raw || '').trim().toLowerCase();
  if (s === 'layout_plank' || s === 'палуба' || s === 'plank') return 'layout_plank';
  if (s === 'layout_herringbone' || s === 'ёлочка' || s === 'елочка' || s === 'herringbone') return 'layout_herringbone';
  if (s === 'layout_tile' || s === 'плитка' || s === 'tile') return 'layout_tile';
  return raw; // unknown — keep as-is
}

// Component for the product card to handle local state for sizes
const CatalogProductCard: React.FC<{ 
  product: Product, 
  navigate: (path: string) => void, 
  index: number, 
  onNavigate: () => void,
  isMobile?: boolean
}> = ({ product, navigate, index, onNavigate, isMobile }) => {
  const { t, language } = useLanguage();
  const [sizeIndex, setSizeIndex] = useState(0);
  const isStone = product.palette === 'STONE';
  const hasSizes = product.sizesTile && product.sizesTile.length > 0 && !isStone;

  const handleSizeClick = (e: React.MouseEvent) => {
    if (hasSizes) {
      e.stopPropagation();
      setSizeIndex((prev) => (prev + 1) % product.sizesTile!.length);
    }
  };

  const getLocalizedName = (name: string | LocalizedText) => {
    if (typeof name === 'string') {
      return t(`product.${product.id}.name`) !== `product.${product.id}.name` ? t(`product.${product.id}.name`) : name;
    }
    return name[language as keyof LocalizedText] || name.ru;
  };

  const cardSpecs = getCardSpecs(product, language, t);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ 
        opacity: 1, 
        y: 0,
        transition: {
          delay: index * 0.05,
          duration: 0.5,
          ease: [0.21, 1.02, 0.47, 0.98]
        }
      }}
      exit={{ 
        opacity: 0, 
        y: 10,
        transition: {
          duration: 0.3,
          ease: "easeIn"
        }
      }}
      layout
      onClick={() => {
        onNavigate();
        trackClick(`catalog-product-${product.slug}`, `/${language}/catalog`);
        const seriesSlug = SERIES_TO_SLUG[product.series] || 'spc-laminate';
        navigate(`/${language}/catalog/${seriesSlug}/${product.slug}`);
      }}
      onContextMenu={(e) => isMobile && e.preventDefault()}
      className={cn(
        "relative overflow-hidden group aspect-[2/3.5] flex flex-col bg-[#1A1A1A] border border-white/5 cursor-pointer catalog-card",
        isMobile && "aspect-[2/3.8] select-none touch-callout-none tap-highlight-transparent touch-manipulation"
      )}
    >
      {/* Background Image/Gradient */}
      <div className="absolute inset-0">
        <img
          src={product.image}
          alt={product.name}
          // First 6 cards are likely above the fold on a typical viewport;
          // load them eagerly with high priority so the WebPs start downloading
          // immediately. Cards further down stay lazy.
          loading={index < 6 ? 'eager' : 'lazy'}
          {...(index < 4 ? { fetchPriority: 'high' as const } : {})}
          decoding="async"
          draggable="false"
          className="w-full h-full object-cover transition-all duration-1000 lg:group-hover:scale-105"
        />
        {/* Permanent Dark Overlay for Readability */}
        <div 
          className="absolute inset-x-0 bottom-0 h-[45%] transition-opacity duration-500 pointer-events-none z-[5] opacity-100"
          style={{
            background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0.2) 80%, rgba(0,0,0,0) 100%)'
          }}
        />
        {/* Mirror Reflection Overlay */}
        <div className="mirror-reflection opacity-40 lg:group-hover:opacity-60 transition-opacity duration-500" />
        <div className="shine-overlay lg:group-hover:opacity-100 transition-opacity duration-700" />

        {/* Hover intensification */}
        <div 
          className="absolute inset-x-0 bottom-0 h-[45%] transition-opacity duration-500 pointer-events-none z-[6] opacity-0 lg:group-hover:opacity-100"
          style={{
            background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0) 100%)'
          }}
        />
      </div>
      
      {/* Content Overlay */}
      <div className="relative h-full flex flex-col z-10">
        {/* Top Series Info */}
        <div className={cn("p-4 lg:p-6", isMobile && "p-1.5")}>
          <span className={cn(
            "font-black uppercase tracking-[0.3em] block lg:group-hover:text-primary transition-colors",
            isMobile ? "text-[5px]" : "text-[7px] lg:text-[8px]"
          )}>
            {product.series}
          </span>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Bottom Info Section */}
        <div className={cn("p-4 lg:p-6 space-y-4 lg:space-y-6", isMobile && "p-2 space-y-2")}>
          <h3 className={cn(
            "font-black uppercase tracking-tight text-white lg:group-hover:text-primary transition-colors",
            isMobile ? "text-[8px] leading-tight" : "text-lg lg:text-xl"
          )}>
            {getLocalizedName(product.name)}
          </h3>

          {/* Specs Grid */}
          <div className={cn(
            "grid grid-cols-3 gap-2 lg:gap-4 pt-4 border-t border-white/10",
            isMobile && "pt-1.5 gap-1"
          )}>
            {cardSpecs.map((spec, idx) => (
              <div key={idx} className="space-y-1">
                <span className={cn(
                  "block font-black uppercase tracking-wider card-label",
                  isMobile ? "text-[4px]" : "text-[6px] lg:text-[7px]"
                )}>
                  {t(spec.labelKey)}
                </span>
                <span className={cn(
                  "block font-black card-meta [text-shadow:0_1px_6px_rgba(0,0,0,0.35)]",
                  isMobile ? "text-[6px]" : "text-[8px] lg:text-[9px]"
                )}>
                  {spec.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Bar (Code) */}
        <div className={cn("bg-black/40 py-2 px-4 lg:px-6 border-t border-white/5", isMobile && "py-1 px-2")}>
          <span className={cn(
            "font-black card-sku uppercase tracking-[0.2em] [text-shadow:0_1px_6px_rgba(0,0,0,0.35)]",
            isMobile ? "text-[6px]" : "text-[8px] lg:text-[9px]"
          )}>
            {product.code}
          </span>
        </div>
      </div>

      {/* Hover Overlay Reflection */}
      <div className="absolute inset-0 bg-gradient-to-tr from-white/5 via-transparent to-transparent opacity-0 lg:group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
      
      {/* Active Border Bottom */}
      <div className="absolute left-0 bottom-0 w-0 h-[1px] bg-primary lg:group-hover:w-full transition-all duration-700 shadow-[0_0_10px_rgba(176,138,90,0.5)]" />
    </motion.div>
  );
};

// ─── Series <-> slug mapping ─────────────────────────────────────────────────
const SERIES_TO_SLUG: Record<string, string> = {
  'SPC LAMINATE': 'spc-laminate',
  'SPC PANELS': 'spc-panels',
  'CARPET TILE': 'carpet-tile',
  'ACCESSORIES': 'accessories',
};

const SLUG_TO_SERIES: Record<string, string> = {
  'spc-laminate': 'SPC LAMINATE',
  'spc-panels': 'SPC PANELS',
  'carpet-tile': 'CARPET TILE',
  'accessories': 'ACCESSORIES',
};

export const Catalog: React.FC<{ seoEnabled?: boolean }> = ({ seoEnabled = true }) => {
  const { t, language } = useLanguage();
  const catalogHero = usePageHero({
    pageKey: 'catalog',
    lang: language,
    fallbacks: {
      eyebrow: t('page.catalog.tagline'),
      title: t('page.catalog.title'),
    },
  });
  // IMPORTANT: useParams() returns {} when this <Catalog /> is rendered as
  // one of the swipe pages inside <MobileTabNavigation> — that component
  // mounts Catalog OUTSIDE the matched `<Route path="catalog/:category">`,
  // so the URL category isn't visible via params. We therefore derive the
  // category slug from `useLocation().pathname` directly, which always
  // reflects the live URL no matter where Catalog is rendered.
  const routeParams = useParams<{ category?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const categoryFromLocation = (() => {
    // Match `/<lang>/catalog/<slug>` or `/catalog/<slug>` — pick the slug.
    const parts = location.pathname.split('/').filter(Boolean);
    const catIdx = parts.indexOf('catalog');
    if (catIdx >= 0 && parts[catIdx + 1]) return parts[catIdx + 1];
    return undefined;
  })();
  const categoryParam = routeParams.category ?? categoryFromLocation;

  // Determine active series from URL path param
  const getInitialSeries = () => {
    if (categoryParam && SLUG_TO_SERIES[categoryParam]) {
      return SLUG_TO_SERIES[categoryParam];
    }
    return 'SPC LAMINATE';
  };

  const [activeSeries, setActiveSeries] = useState<string>(getInitialSeries());
  const [activePalette, setActivePalette] = useState<string>('ALL');
  const [activeInstallationType, setActiveInstallationType] = useState<string>('ALL');
  const [activeGroupType, setActiveGroupType] = useState<string>('ALL');
  const [activeCollection, setActiveCollection] = useState<string>('ALL');
  const [activeAccessoryCategory, setActiveAccessoryCategory] = useState<string>('ALL');
  const productsGridRef = React.useRef<HTMLDivElement>(null);

  // Load products — synchronously hydrate from cache so the cards (and
  // their WebP images) render on first paint instead of waiting for the
  // async DB fetch. The fetch still runs in the background to refresh the
  // cache if it's stale.
  const [products, setProducts] = useState<Product[] | null>(() => getCachedProducts() ?? null);
  useEffect(() => {
    // Fetch (uses cache if fresh, otherwise fetches from DB)
    fetchProducts().then((data) => { setProducts(data); });

    // Subscribe to cache updates
    const unsub = subscribeToCache(() => {
      const updated = getCachedProducts();
      if (updated) setProducts(updated);
    });
    return unsub;
  }, []);
  const [isMobileView, setIsMobileView] = useState(typeof window !== 'undefined' ? window.innerWidth <= 1024 : false);
  // Previously this was true only on small phones (<=640) which meant
  // tablets (641-1024) saw the intermediate "4-card category picker" screen.
  // The user prefers going straight to SPC Laminate on ALL mobile/tablet
  // widths, so we now mirror isMobileView.
  const [mobileCategorySelected, setMobileCategorySelected] = useState(typeof window !== 'undefined' ? window.innerWidth <= 1024 : false);

  // ─── Pagination ────────────────────────────────────────────────────────────
  // Hard cap on DOM size — large series (e.g. 100+ SPC laminate items)
  // previously rendered all cards at once which caused a major DOM bloat.
  const PAGE_SIZE = 20;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const checkWidth = () => {
      const width = window.innerWidth;
      setIsMobileView(width <= 1024);
    };
    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  const headerVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    })
  };

  const saveContext = () => {
    let scrollY = window.scrollY;
    
    // If on mobile and in a main page, the scroll container is .mobile-swipe-page
    const pageContainer = productsGridRef.current?.closest('.mobile-swipe-page');
    if (pageContainer) {
      scrollY = pageContainer.scrollTop;
    }

    const context = {
      activeSeries,
      activePalette,
      activeInstallationType,
      activeGroupType,
      activeCollection,
      activeAccessoryCategory,
      scrollY
    };
    sessionStorage.setItem('catalogReturnContext', JSON.stringify(context));
  };

  // Sync activeSeries with URL path param changes and restore context
  useEffect(() => {
    const savedContextStr = sessionStorage.getItem('catalogReturnContext');
    
    if (categoryParam && SLUG_TO_SERIES[categoryParam]) {
      // Path-based category: /catalog/:category
      const targetSeries = SLUG_TO_SERIES[categoryParam];
      setActiveSeries(prev => {
        // When the user navigates to a DIFFERENT category, all sub-filters
        // (palette, installation type, group type, collection, accessory
        // category) MUST be reset — they belong to the previous category and
        // their values usually don't exist in the new one, producing an
        // empty product list until the user manually clears them. This was
        // a confusing UX bug ("после смены категории пусто, пока не сбросишь").
        if (prev !== targetSeries) {
          setActivePalette('ALL');
          setActiveInstallationType('ALL');
          setActiveGroupType('ALL');
          setActiveCollection('ALL');
          setActiveAccessoryCategory('ALL');
        }
        return targetSeries;
      });
      if (window.innerWidth <= 1024) {
        setMobileCategorySelected(true);
      }
    } else if (!categoryParam && savedContextStr) {
      // On /catalog with saved context — restore filters + scroll
      try {
        const savedContext = JSON.parse(savedContextStr);
        setActiveSeries(savedContext.activeSeries);
        setActivePalette(savedContext.activePalette);
        setActiveInstallationType(savedContext.activeInstallationType);
        setActiveGroupType(savedContext.activeGroupType);
        setActiveCollection(savedContext.activeCollection);
        setActiveAccessoryCategory(savedContext.activeAccessoryCategory || 'ALL');
        
        requestAnimationFrame(() => {
          const pageContainer = productsGridRef.current?.closest('.mobile-swipe-page');
          if (pageContainer) {
            pageContainer.scrollTo({ top: savedContext.scrollY, behavior: 'instant' });
          } else {
            window.scrollTo({ top: savedContext.scrollY, behavior: 'instant' });
          }
        });
        
        sessionStorage.removeItem('catalogReturnContext');
      } catch (e) {
        console.error('Failed to restore catalog context', e);
      }
    } else if (!categoryParam) {
      // /catalog with no saved context — default
      setActiveSeries('SPC LAMINATE');
      setActivePalette('ALL');
      setActiveInstallationType('ALL');
      setActiveGroupType('ALL');
      setActiveCollection('ALL');
      setActiveAccessoryCategory('ALL');
      if (window.innerWidth <= 1024) {
        setMobileCategorySelected(true);
      }
    }
  }, [categoryParam]); // Depend on path param only

  const seriesTabs = [
    { id: 'SPC LAMINATE', label: t('catalog.series.primary'), desc: t('catalog.series.pro.desc') },
    { id: 'SPC PANELS', label: t('catalog.series.element'), desc: t('catalog.series.home.desc') },
    { id: 'CARPET TILE', label: t('catalog.series.carpet'), desc: t('catalog.series.carpet.desc') },
    { id: 'ACCESSORIES', label: t('catalog.series.system'), desc: t('catalog.series.system.desc') },
  ];

  const handleSeriesChange = (id: string) => {
    const slug = SERIES_TO_SLUG[id] || 'spc-laminate';
    navigate(`/${language}/catalog/${slug}`, { replace: true });
    
    if (isMobileView) {
      setMobileCategorySelected(true);
      const gridElement = productsGridRef.current;
      if (gridElement) {
        const pageContainer = gridElement.closest('.mobile-swipe-page');
        if (pageContainer) {
          pageContainer.scrollTo({ top: 0, behavior: 'instant' });
        }
      }
    }
  };

  const allProducts = products ?? [];

  const filteredProducts = useMemo(() => {
    const filtered = allProducts.filter(p => {
      // 1. Series Filter
      if (activeSeries !== 'ALL' && p.series !== activeSeries) return false;
      
      // 2. Palette Filter (Reusable for SPC PANELS colorTag)
      if (activePalette !== 'ALL') {
        if (activeSeries === 'SPC PANELS') {
          if (p.colorTag !== activePalette) return false;
        } else if (activeSeries !== 'ACCESSORIES') {
          if (p.palette !== activePalette) return false;
        }
      }
      
      // 3. Installation Type Filter (Laying Type) — normalize both sides
      if (activeSeries !== 'SPC PANELS' && activeSeries !== 'CARPET TILE' && activeSeries !== 'ACCESSORIES' && activeInstallationType !== 'ALL') {
        const normFilter = normalizeLayingType(activeInstallationType);
        const matchesType = normalizeLayingType(p.installationType ?? '') === normFilter;
        const matchesLaying = p.layingTypes?.some(type => normalizeLayingType(type) === normFilter);
        if (!matchesType && !matchesLaying) return false;
      }

      // 4. Group Type Filter - Only for SPC PANELS
      if (activeSeries === 'SPC PANELS' && activeGroupType !== 'ALL') {
        if (p.groupType !== activeGroupType) return false;
      }

      // 5. Collection Filter - Only for CARPET TILE (case-insensitive for backward compat)
      if (activeSeries === 'CARPET TILE' && activeCollection !== 'ALL') {
        if ((p.collection ?? '').toUpperCase() !== activeCollection.toUpperCase()) return false;
      }

      // 6. Accessory Category Filter
      if (activeSeries === 'ACCESSORIES' && activeAccessoryCategory !== 'ALL') {
        if (!p.compatibility || !p.compatibility.includes(activeAccessoryCategory)) return false;
      }
      
      return true;
    });
    // Для SPC LAMINATE при фильтре "Все" — коллекция WHITE всегда первая
    const sorted = [...filtered].sort(naturalSortByCode);
    if (activeSeries === 'SPC LAMINATE' && activePalette === 'ALL') {
      sorted.sort((a, b) => {
        const aIsWhite = a.palette === 'WHITE' ? 0 : 1;
        const bIsWhite = b.palette === 'WHITE' ? 0 : 1;
        if (aIsWhite !== bIsWhite) return aIsWhite - bIsWhite;
        return naturalSortByCode(a, b);
      });
    }
    return sorted;
  }, [allProducts, activeSeries, activePalette, activeInstallationType, activeGroupType, activeCollection, activeAccessoryCategory]);

  // Reset to first page whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeSeries, activePalette, activeInstallationType, activeGroupType, activeCollection, activeAccessoryCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  // Guard: if currentPage falls outside the range (e.g. items were removed), clamp it.
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedProducts = useMemo(() => {
    const start = (safeCurrentPage - 1) * PAGE_SIZE;
    return filteredProducts.slice(start, start + PAGE_SIZE);
  }, [filteredProducts, safeCurrentPage]);

  const palettes = ['ALL', 'WHITE', 'GREY', 'NATURE LIGHT', 'NATURE MEDIUM', 'NATURE DARK', 'STONE'];
  const panelPalettes = ['ALL', 'БЕЛЫЙ', 'СВЕТЛЫЙ', 'СРЕДНИЙ', 'СЕРЫЙ', 'ТЁМНЫЙ', 'КОРИЧНЕВЫЙ', 'СИНЕ-СЕРЫЙ'];
  const panelGroups = ['ALL', 'WD', 'STP'];
  // Canonical laying type keys (normalized)
  const installationTypes = ['ALL', 'layout_tile', 'layout_herringbone', 'layout_plank'];
  // Uppercase to match DB values saved by the admin dropdown
  const carpetCollections = ['ALL', 'GRAVITY', 'LINES'];
  const accessoryCategories = ['ALL', 'SPC LAMINATE', 'SPC PANELS', 'CARPET TILE'];

  // Memoized count calculation functions for faceted filtering — reactive to allProducts
  const getCollectionCount = useCallback((collection: string) => {
    return allProducts.filter(p => 
      p.series === 'CARPET TILE' && 
      (collection === 'ALL' || (p.collection ?? '').toUpperCase() === collection.toUpperCase())
    ).length;
  }, [allProducts]);

  const getPaletteCount = useCallback((palette: string) => {
    return allProducts.filter(p => {
      if (p.series !== activeSeries) return false;
      if (activeSeries === 'SPC LAMINATE' && activeInstallationType !== 'ALL') {
        const normFilter = normalizeLayingType(activeInstallationType);
        const matchesType = normalizeLayingType(p.installationType ?? '') === normFilter;
        const matchesLaying = p.layingTypes?.some(type => normalizeLayingType(type) === normFilter);
        if (!matchesType && !matchesLaying) return false;
      }
      return palette === 'ALL' || p.palette === palette;
    }).length;
  }, [allProducts, activeSeries, activeInstallationType]);

  const getInstallationTypeCount = useCallback((installationType: string) => {
    return allProducts.filter(p => {
      if (p.series !== activeSeries) return false;
      if (activePalette !== 'ALL' && p.palette !== activePalette) return false;
      if (installationType === 'ALL') return true;
      const normFilter = normalizeLayingType(installationType);
      const matchesType = normalizeLayingType(p.installationType ?? '') === normFilter;
      const matchesLaying = p.layingTypes?.some(type => normalizeLayingType(type) === normFilter);
      return matchesType || matchesLaying;
    }).length;
  }, [allProducts, activeSeries, activePalette]);

  const getGroupTypeCount = useCallback((groupType: string) => {
    return allProducts.filter(p => {
      if (p.series !== 'SPC PANELS') return false;
      if (activePalette !== 'ALL' && p.colorTag !== activePalette) return false;
      return groupType === 'ALL' || p.groupType === groupType;
    }).length;
  }, [allProducts, activePalette]);

  const getPanelPaletteCount = useCallback((palette: string) => {
    return allProducts.filter(p => {
      if (p.series !== 'SPC PANELS') return false;
      if (activeGroupType !== 'ALL' && p.groupType !== activeGroupType) return false;
      return palette === 'ALL' || p.colorTag === palette;
    }).length;
  }, [allProducts, activeGroupType]);

  const getAccessoryCategoryCount = useCallback((category: string) => {
    return allProducts.filter(p => {
      if (p.series !== 'ACCESSORIES') return false;
      if (category === 'ALL') return true;
      return p.compatibility && p.compatibility.includes(category);
    }).length;
  }, [allProducts]);

  // Smooth scroll to products grid when filters change
  useEffect(() => {
    if (!productsGridRef.current) return;

    const gridElement = productsGridRef.current;
    const gridTop = gridElement.getBoundingClientRect().top + window.scrollY;
    const headerOffset = 140; // Sticky header height + small offset
    const scrollTarget = gridTop - headerOffset;
    const currentScroll = window.scrollY;

    // Only scroll if user is below the grid start position
    if (currentScroll > scrollTarget) {
      window.scrollTo({
        top: scrollTarget,
        behavior: 'smooth'
      });
    }
  }, [activeSeries, activePalette, activeInstallationType, activeGroupType, activeCollection, activeAccessoryCategory]);

  return (
    <div className="min-h-screen bg-background text-white">
      <SEO
        enabled={seoEnabled}
        title={language === 'uz' ? "SARGO katalogi | SPC laminat, SPC panellar va gilam plitka" : language === 'en' ? 'SARGO Catalogue | SPC Laminate, SPC Panels and Carpet Tile' : 'Каталог SARGO | SPC-ламинат, SPC-панели и ковровая плитка'}
        description={language === 'uz'
          ? "SARGO mahsulotlari katalogi: SPC laminat, SPC panellar, gilam plitka va aksessuarlar. Kolleksiyalar, xususiyatlar va turar-joy hamda tijorat interyer yechimlarini tanlash."
          : language === 'en'
            ? 'SARGO product catalogue: SPC laminate, SPC panels, carpet tile and accessories. Collections, specs and solutions for residential and commercial interiors.'
            : 'Каталог продукции SARGO: SPC-ламинат, SPC-панели, ковровая плитка и аксессуары. Коллекции, характеристики и подбор решений для жилых и коммерческих интерьеров.'}
        canonical={categoryParam && SERIES_TO_SLUG[SLUG_TO_SERIES[categoryParam] || ''] ? `/${language}/catalog/${categoryParam}` : `/${language}/catalog`}
        schema={(() => {
          const base = window.location.origin;
          const categoryName = categoryParam && SLUG_TO_SERIES[categoryParam]
            ? SLUG_TO_SERIES[categoryParam]
            : null;
          return [
            buildCollectionPage(base),
            buildBreadcrumbs(base, [
              { name: language === 'uz' ? 'Bosh sahifa' : language === 'en' ? 'Home' : 'Главная', url: `/${language}` },
              { name: language === 'uz' ? 'Katalog' : language === 'en' ? 'Catalogue' : 'Каталог', url: `/${language}/catalog` },
              ...(categoryName ? [{ name: categoryName, url: `/${language}/catalog/${categoryParam}` }] : []),
            ]),
          ];
        })()}
      />
      {/* Mobile Category Sticky Navigation */}
      {isMobileView && mobileCategorySelected && (
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-white/5 h-[52px] px-2 flex items-center justify-center mirror-style w-full shrink-0">
          <div className="grid grid-cols-4 gap-1 w-full max-w-full">
            {seriesTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleSeriesChange(tab.id)}
                className={cn(
                  "px-0.5 py-1.5 text-[8.5px] md:text-[6.5px] font-black uppercase tracking-tighter transition-all duration-300 border flex items-center justify-center text-center leading-[1.1] min-w-0 overflow-hidden",
                  activeSeries === tab.id
                    ? "bg-primary text-white border-primary shadow-[0_0_10px_rgba(176,138,90,0.4)]"
                    : "bg-white/5 text-white/50 border-white/10"
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <section className={cn(
        "pt-24 md:pt-32 pb-12 lg:pb-16 relative",
        isMobileView && mobileCategorySelected && "hidden"
      )}>
        <div className="grid-cells opacity-50" />
        <div className="container-custom relative z-10">
          <div className="space-y-4 lg:space-y-6">
            {catalogHero.eyebrow?.trim() && (
              <motion.div 
                custom={0}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={headerVariants}
                className="flex items-center gap-2 lg:gap-3"
              >
                <div className="w-1 h-1 lg:w-1.5 lg:h-1.5 bg-primary shadow-[0_0_10px_rgba(176,138,90,1)]"></div>
                <p className="text-primary font-black uppercase tracking-[0.4em] text-[10px]">{catalogHero.eyebrow}</p>
              </motion.div>
            )}
            {catalogHero.title?.trim() && (
              <motion.h1 
                custom={1}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={headerVariants}
                className="font-black uppercase tracking-tighter leading-[0.95] lg:leading-[0.8]"
              >
                {catalogHero.title}<span className="text-primary">.</span>
              </motion.h1>
            )}
            {catalogHero.subtitle?.trim() && (
              <motion.p 
                custom={2}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={headerVariants}
                className="text-white/40 max-w-xl text-sm md:text-base lg:text-lg leading-relaxed font-black uppercase tracking-wider"
              >
                {catalogHero.subtitle}
              </motion.p>
            )}
          </div>
        </div>
      </section>
      
      <section className="pb-16 lg:pb-32 relative">
        <div className="container-custom relative z-10">
          {/* Series Cards */}
          <div className={cn(
            "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12 lg:mb-16",
            isMobileView && mobileCategorySelected && "hidden"
          )}>
            {seriesTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleSeriesChange(tab.id)}
                className={cn(
                  "p-5 lg:p-6 border text-left transition-all duration-300 group relative overflow-hidden min-h-[80px] lg:min-h-[100px]",
                  activeSeries === tab.id 
                    ? "border-primary bg-primary/5" 
                    : "border-white/10 hover:border-primary/50"
                )}
              >
                <div className="space-y-2 relative z-10">
                  <h3 className={cn(
                    "text-[10px] lg:text-xs font-black uppercase tracking-[0.2em] transition-colors",
                    activeSeries === tab.id ? "text-primary" : "text-white"
                  )}>
                    {tab.label}
                  </h3>
                  <p className="text-[9px] lg:text-[10px] text-white/40 font-black uppercase tracking-wider leading-relaxed line-clamp-2">
                    {tab.desc}
                  </p>
                </div>
                {activeSeries === tab.id && (
                  <div className="absolute top-0 right-0 w-1 h-full bg-primary shadow-[0_0_15px_rgba(176,138,90,0.5)]" />
                )}
              </button>
            ))}
          </div>
      
          <div className={cn(
            "flex flex-col gap-8",
            !isMobileView && (activeSeries === 'SPC LAMINATE' || activeSeries === 'SPC PANELS' || activeSeries === 'CARPET TILE' || activeSeries === 'ACCESSORIES')
              ? "lg:grid lg:grid-cols-[16rem_1fr] xl:grid-cols-[18rem_1fr] 2xl:grid-cols-[16rem_1fr] lg:gap-8 2xl:gap-12"
              : ""
          )}>
            {/* Sidebar Filters */}
            {!isMobileView && (activeSeries === 'SPC LAMINATE' || activeSeries === 'SPC PANELS' || activeSeries === 'CARPET TILE' || activeSeries === 'ACCESSORIES') && (
              <div className="hidden lg:block sticky top-32 self-start max-h-[calc(100vh-10rem)]">
              <CatalogFilter
                activeSeries={activeSeries}
                activePalette={activePalette}
                activeInstallationType={activeInstallationType}
                activeGroupType={activeGroupType}
                activeCollection={activeCollection}
                activeAccessoryCategory={activeAccessoryCategory}
                onPaletteChange={setActivePalette}
                onInstallationTypeChange={setActiveInstallationType}
                onGroupTypeChange={setActiveGroupType}
                onCollectionChange={setActiveCollection}
                onAccessoryCategoryChange={setActiveAccessoryCategory}
                onReset={() => {
                  setActivePalette('ALL');
                  setActiveInstallationType('ALL');
                  setActiveGroupType('ALL');
                  setActiveCollection('ALL');
                  setActiveAccessoryCategory('ALL');
                }}
                getCollectionCount={getCollectionCount}
                getPaletteCount={getPaletteCount}
                getInstallationTypeCount={getInstallationTypeCount}
                getGroupTypeCount={getGroupTypeCount}
                getPanelPaletteCount={getPanelPaletteCount}
                getAccessoryCategoryCount={getAccessoryCategoryCount}
              />
              </div>
            )}

            {/* Main Grid */}
            <div 
              ref={productsGridRef}
              className="w-full min-w-0"
            >
              {/* No loading skeleton — data comes from global cache instantly */}

              <AnimatePresence mode="wait">
                {products !== null && (!isMobileView || mobileCategorySelected) && (
                  <motion.div 
                    key={`${activeSeries}-${activePalette}-${activeInstallationType}-${activeCollection}-${activeAccessoryCategory}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className={cn(
                      "grid gap-[1px]",
                      isMobileView 
                        ? "grid-cols-3" 
                        : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3"
                    )}
                  >
                    {paginatedProducts.map((product, index) => (
                      <CatalogProductCard
                        key={`${product.id}-${activeSeries}`}
                        product={product}
                        navigate={navigate}
                        index={index}
                        isMobile={isMobileView}
                        onNavigate={saveContext}
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Pagination — only renders when totalPages > 1 */}
              {filteredProducts.length > PAGE_SIZE && (
                <div className="mt-8 lg:mt-12">
                  <Pagination
                    currentPage={safeCurrentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                      setCurrentPage(page);
                      // Smooth scroll to top of grid on page change
                      if (typeof window !== 'undefined') {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                  />
                </div>
              )}

              {products !== null && (!isMobileView || mobileCategorySelected) && filteredProducts.length === 0 && (
                <div className="py-40 text-center mirror-card">
                  <p className="text-white/30 uppercase tracking-[0.2em] font-black">{t('catalog.empty')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};